import { all, call, put, takeEvery } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { logSagaErrorMessage } from 'utils';
import {
	requestSagaFactory,
	getAdminEndpoint,
	getEndpointWithId,
} from 'helpers';
import { IRequestParams, request } from 'helpers/request';
import { IsoString } from 'models';
import {
	INotificationPayload,
	ISurveyTrackingData,
	ISurveyRoundPayload,
	ISurveyRoundResponse,
} from 'modules/admin-panel/pages/settings/models';
import * as types from 'modules/admin-panel/pages/settings/ActionTypes';
import {
	getTrackingSurveyList,
	getStopSurveyRound,
	getStopSurvey,
	deleteSurvey,
	getSurveySent,
	getAllTrackingSurveyList,
	sendNotifications,
	editSurveyRound,
	getSurveyRound,
} from 'modules/admin-panel/pages/settings/settings.actions';

const sendNotificationsPath =
	'/surveys/round/:replaceRoundId/sendNotifications';
const sendNotificationsUrl = getEndpointWithId(
	getAdminEndpoint(sendNotificationsPath),
);
const editSurveyRoundPath = '/surveys/round/:replaceRoundId';
const editSurveyRoundUrl = getEndpointWithId(
	getAdminEndpoint(editSurveyRoundPath),
);

const getAllTrackingSurveyListSaga = requestSagaFactory<
	{ page: number; size: number },
	ISurveyTrackingData
>({
	url: getAdminEndpoint('/surveys'),
	actionApi: getAllTrackingSurveyList,
	logErrorMessage: 'getAllLastSentSurveyList',
});

const getTrackingSurveyListSaga = requestSagaFactory<
	{ clientId: string; page: number; size: number },
	ISurveyTrackingData
>({
	url: getAdminEndpoint('/surveys'),
	actionApi: getTrackingSurveyList,
	logErrorMessage: 'getLastSentSurveyList',
});

const getGraphicData = requestSagaFactory<{}, Record<IsoString, number>>({
	url: getAdminEndpoint('/surveys/sent'),
	actionApi: getSurveySent,
	logErrorMessage: 'getSurveySent',
});

const deleteSurveySaga = requestSagaFactory<
	{ surveyId: string },
	{ surveyId: string }
>({
	url: getAdminEndpoint('/surveys/{{surveyId}}'),
	actionApi: deleteSurvey,
	logErrorMessage: 'deleteSurvey',
	method: 'DELETE',
});

const getStopSurveySaga = requestSagaFactory<
	{ surveyId: string },
	{ surveyId: string }
>({
	url: getAdminEndpoint('/surveys/{{surveyId}}/stop'),
	actionApi: getStopSurvey,
	logErrorMessage: 'getStopSeries',
});

const getStopSurveyRoundSaga = requestSagaFactory<
	{ surveyRoundId: string },
	{}
>({
	url: getAdminEndpoint('/surveys/round/{{surveyRoundId}}/stop'),
	actionApi: getStopSurveyRound,
	logErrorMessage: 'getStopSurvey',
});

const getSurveyRoundSaga = requestSagaFactory<{ id: string }, {}>({
	url: getAdminEndpoint('/surveys/round/{{id}}'),
	actionApi: getSurveyRound,
	logErrorMessage: 'getSurveyRound',
});

function* editSurveyRoundSaga({
	payload,
}: {
	payload: ISurveyRoundPayload;
	type: string;
}) {
	try {
		yield put(editSurveyRound.loading());

		const editSurveyRoundParams: IRequestParams = {
			method: 'PATCH',
			url: editSurveyRoundUrl(payload.id),
			data: JSON.stringify(payload),
		};

		const { data }: AxiosResponse<ISurveyRoundResponse> = yield call(
			request,
			editSurveyRoundParams,
		);

		yield put(editSurveyRound.success(data));
	} catch (e) {
		logSagaErrorMessage('edit survey round', e);
		yield put(editSurveyRound.fail(e));
	}
}

function* sendNotificationsSaga({
	payload,
}: {
	type: string;
	payload: INotificationPayload;
}) {
	try {
		yield put(sendNotifications.loading());

		const { roundId, country, city } = payload;

		const getSendNotificationsParams: IRequestParams = {
			url: sendNotificationsUrl(roundId),
			params: {
				roundId,
				country,
				city,
			},
		};

		const getAllTrackingSurveyListParams: IRequestParams = {
			url: getAdminEndpoint('/surveys'),
			params: {},
		};

		yield call(request, getSendNotificationsParams);
		const { data }: AxiosResponse<ISurveyTrackingData[]> = yield call(
			request,
			getAllTrackingSurveyListParams,
		);

		yield put(sendNotifications.success(data));
	} catch (e) {
		logSagaErrorMessage('send notifications', e);
		yield put(sendNotifications.fail(e));
	}
}

export function* surveyTrackingSaga() {
	yield all([
		takeEvery(
			types.GET_TRACKING_SURVEY_LIST.DEFAULT,
			getTrackingSurveyListSaga,
		),
		takeEvery(
			types.GET_ALL_TRACKING_SURVEY_LIST.DEFAULT,
			getAllTrackingSurveyListSaga,
		),
		takeEvery(types.GET_SURVEY_SENT.DEFAULT, getGraphicData),
		takeEvery(types.GET_STOP_SURVEY_ROUND.DEFAULT, getStopSurveyRoundSaga),
		takeEvery(types.GET_STOP_SURVEY.DEFAULT, getStopSurveySaga),
		takeEvery(types.DELETE_SURVEY.DEFAULT, deleteSurveySaga),
		takeEvery(types.GET_SEND_NOTIFICATIONS.DEFAULT, sendNotificationsSaga),
		takeEvery(types.GET_SURVEY_ROUND.DEFAULT, getSurveyRoundSaga),
		takeEvery(types.EDIT_SURVEY_ROUND.DEFAULT, editSurveyRoundSaga),
	]);
}
